import useCarousel from '@v2/shared/lib/useCarousel';
import { Product } from '@v2/shared/lib/types';

import SliderSection from '@v2/shared/components/SliderSection';
import ProductCard, { coverAspectFor } from '@v2/shared/components/ProductCard';

type Props = {
  /**
   * Omitted when the band is the only thing in a section that already names it — a
   * heading repeated directly under its own heading reads as a mistake.
   */
  title?: string;
  viewAllLink?: string;
  products?: Product[];
  /** Passed to each card; see ProductCard. Off gives a cover-and-name browse strip. */
  showDetails?: boolean;
  /** Passed to each card; see ProductCard. */
  openInNewTab?: boolean;
  /** Passed to each card; see ProductCard. */
  showSavings?: boolean;
  /** Passed to each card; see ProductCard. */
  showMsrp?: boolean;
  /**
   * The GA4 `item_list_name` the cards report under. Defaults to the heading; a band whose
   * heading changes with the page — one that names the visitor's search — gives a list
   * name that does not, so its impressions stay one list in the reports.
   */
  list?: string;
};

export default function ProductBand({
  title,
  viewAllLink,
  products,
  showDetails = true,
  openInNewTab = false,
  showSavings = true,
  showMsrp = true,
  list = title,
}: Props) {
  const { containerRef, firstItemRef, lastItemRef, ...carouselProps } = useCarousel();

  if (!products || products.length === 0) return null;

  // Decided once, here, and handed to every card. Per card it would defeat itself: a
  // row holding both books and magazines would give each a different box and the text
  // under them would go ragged again, which is what the box exists to prevent.
  const coverAspect = coverAspectFor(products);

  return (
    <SliderSection title={title} viewAllLink={viewAllLink} {...carouselProps}>
      <ul className="flex gap-4 overflow-x-auto w-full snap-x scrollbar-hide" ref={containerRef}>
        {products.map((product, i) => {
          // A card without details renders nothing from the sku, so only the priced
          // one needs it.
          if (showDetails && !product.base_sku) {
            return null;
          }

          return (
            <li
              key={product.id}
              ref={i === 0 ? firstItemRef : i === products.length - 1 ? lastItemRef : undefined}
              className="snap-start snap-normal shrink-0"
            >
                <ProductCard
                key={product.id}
                product={product}
                showDetails={showDetails}
                coverAspect={coverAspect}
                openInNewTab={openInNewTab}
                showSavings={showSavings}
                showMsrp={showMsrp}
                /*
                 * The band's own heading is the list the cards are reported under — a
                 * homepage band is "Best Sellers", and that is the name the legacy
                 * templates put in `data-list` too. A band with no heading reports
                 * nothing: an impression in a list nobody can name is a row in a report
                 * that cannot be read.
                 *
                 * `index` is the position the server returned, not the position the card
                 * was scrolled to, which is what GA4 means by it.
                 */
                list={list}
                index={i + 1}
                className="w-[130px] mr-2 inline-block"
              />
            </li>
          );
        })}
      </ul>
    </SliderSection>
  );
}
